/* global console, setTimeout, clearTimeout */

import axios from "axios";
import { getAccessToken } from "../utils/storage.utils";
import type { CapturedError } from "../utils/error-reporting";

/**
 * Send crash reports to our own backend.
 *
 * Sentry is a third party and is switched off entirely for firms in Australian
 * mode, so it cannot be the only destination. This is the sovereign path: same
 * API the add-in already uses, under the user's own session.
 *
 * Rules this must never break:
 *  - Never throw. Failing to report an error must not become a second error.
 *  - Never sign the user out. This deliberately uses its OWN axios client, not
 *    the shared one: the shared instance's interceptor treats a 401/403 as an
 *    expired session, attempts a token refresh, and can clear every token and
 *    log the user out. A failed error report must never do that to someone
 *    mid-document.
 *  - Never retry hard. A backend that is down is often the cause of the errors.
 *  - Never send document text, chat content, or credentials. Entries arrive
 *    already sanitised by error-reporting.
 */

const ENDPOINT = "/telemetry/client-error";
const FLUSH_DELAY_MS = 2000;
const MAX_BATCH = 10;
const MAX_QUEUE = 50;
/** Roughly caps a single request; entries are already string-capped upstream. */
const MAX_PAYLOAD_BYTES = 128 * 1024;
/** Give up after this many consecutive failures (e.g. endpoint not deployed). */
const MAX_CONSECUTIVE_FAILURES = 3;

/**
 * Its own client on purpose — no request interceptor, no response interceptor,
 * no refresh, no logout. See the note above.
 */
const telemetryClient = axios.create({
  baseURL: process.env.API_BASE_URL,
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

let queue: CapturedError[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight = false;
let consecutiveFailures = 0;
let disabled = false;

function scheduleFlush(): void {
  timer ??= setTimeout(() => {
    timer = null;
    void flush();
  }, FLUSH_DELAY_MS);
}

async function flush(): Promise<void> {
  // One request at a time, so a burst cannot open several in parallel.
  if (disabled || inFlight || queue.length === 0) return;

  inFlight = true;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);

  try {
    const token = await getAccessToken();
    // Drop whole entries rather than cutting the serialised string, which would
    // put syntactically invalid JSON on the wire — the backend would reject it,
    // the batch would count as a failure, and reporting could disable itself.
    let sending = batch;
    let body = JSON.stringify({ errors: sending });
    while (body.length > MAX_PAYLOAD_BYTES && sending.length > 1) {
      sending = sending.slice(0, -1);
      body = JSON.stringify({ errors: sending });
    }
    await telemetryClient.post(ENDPOINT, body, {
      headers: token != null && token !== "" ? { Authorization: `Bearer ${token}` } : undefined,
    });
    consecutiveFailures = 0;
  } catch (error) {
    consecutiveFailures += 1;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      // Repeated failure usually means the endpoint is not deployed in this
      // environment. Give up quietly rather than posting into the void. Errors
      // are still captured locally and available via Copy diagnostics.
      disabled = true;
      queue = [];
      console.warn("Client error reporting disabled after repeated failures.", error);
    } else {
      // A transient blip should not lose the batch.
      queue = [...batch, ...queue].slice(0, MAX_QUEUE);
    }
  } finally {
    inFlight = false;
    // Anything left over must still go. Without this a burst of twenty errors
    // sent ten and stranded the rest until the next unrelated error.
    if (!disabled && queue.length > 0) scheduleFlush();
  }
}

/**
 * Queue an error for delivery. Batched briefly so a burst of related failures
 * becomes one request rather than ten.
 */
export function sendErrorToBackend(entry: CapturedError): void {
  try {
    if (disabled) return;
    queue.push(entry);
    // Keep the newest. Old errors matter less than staying bounded.
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    scheduleFlush();
  } catch {
    // Reporting must never become the problem.
  }
}

/** Send anything still queued immediately — used when the pane is closing. */
export function flushErrorQueue(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  void flush();
}

/**
 * Drop anything queued under the previous session.
 *
 * Called when the signed-in user changes: otherwise the previous user's errors
 * would be posted using the new user's token, attributing one customer's
 * failures to another.
 */
export function resetTelemetryForUserChange(): void {
  queue = [];
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  consecutiveFailures = 0;
  // NOTE: `disabled` is deliberately NOT reset. If the endpoint is missing in
  // this environment it is still missing after someone signs in again, and
  // re-arming would mean another three failed requests per account switch.
}

/** Test hook: reset the module's internal state. */
export function __resetTelemetryForTests(): void {
  resetTelemetryForUserChange();
  inFlight = false;
}
